import React, { useState } from 'react';
import { useApp } from '../../appContext';
import { formatDate } from '../../utils/scheduling';

export default function AuditMenu() {
  const {
    navigate,
    setSelectedLocation,
    locations,
    isLoading,
    isOnline,
    drafts,
    clearAllDrafts,
    uploadDraft,
    draftConcerns,
    refreshData,
  } = useApp();

  const [uploadingId, setUploadingId] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const handleNewAudit = () => {
    setSelectedLocation(null);
    navigate('inspection-form');
  };

  const handleSelectLocation = (loc: typeof locations[0]) => {
    setSelectedLocation(loc);
    navigate('inspection-form');
  };

  const handleUploadDraft = async (localId: string) => {
    const draft = drafts.find((d) => d.localId === localId);
    if (!draft) return;
    setUploadingId(localId);
    await uploadDraft(draft);
    setUploadingId(null);
    refreshData();
  };

  const handleClearDrafts = () => {
    if (confirmClear) {
      clearAllDrafts();
      setConfirmClear(false);
    } else {
      setConfirmClear(true);
      setTimeout(() => setConfirmClear(false), 3000);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* ── Page header ─────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#4C9E00]">
            Ground Control Audits
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">
            Cory Mine — select a location or start a new audit
          </p>
        </div>

        {/* New Audit button */}
        <button
          onClick={handleNewAudit}
          className="flex-shrink-0 bg-[#4C9E00] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#3D8000] transition flex items-center gap-2 shadow"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          New Audit
        </button>
      </div>

      {/* ── Drafts section ───────────────────────────────────────────────── */}
      {drafts.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Drafts
              <span className="bg-amber-100 text-amber-700 text-xs font-bold px-2 py-0.5 rounded-full">
                {drafts.length}
              </span>
            </h2>
            <button
              onClick={handleClearDrafts}
              className={`text-xs px-3 py-1.5 rounded-md font-medium transition ${
                confirmClear
                  ? 'bg-red-500 text-white hover:bg-red-600'
                  : 'border border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {confirmClear ? 'Confirm Clear' : 'Clear Drafts'}
            </button>
          </div>

          <div className="space-y-2">
            {drafts.map((draft) => {
              const concernCount = draftConcerns.filter(
                (c) => c.parentDraftId === draft.localId
              ).length;
              return (
                <div
                  key={draft.localId}
                  className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{draft.Area}</span>
                      <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                        {draft.AreaType}
                      </span>
                      <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded-full">
                        Draft
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                      Saved {formatDate(draft.savedAt.slice(0, 10))} · {draft.InspectedBy}
                      {concernCount > 0 && ` · ${concernCount} concern${concernCount > 1 ? 's' : ''}`}
                    </p>
                  </div>

                  {/* Upload button (online only) */}
                  {isOnline && (
                    <button
                      onClick={() => handleUploadDraft(draft.localId)}
                      disabled={uploadingId === draft.localId}
                      className="ml-3 flex-shrink-0 p-2 rounded-lg bg-[#4C9E00] text-white hover:bg-[#3D8000] disabled:opacity-50 transition"
                      title="Upload to SharePoint"
                    >
                      {uploadingId === draft.localId ? (
                        <svg className="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Locations section (online only) ─────────────────────────────── */}
      {isOnline && (
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-4 h-4 text-[#4C9E00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Locations
              {!isLoading && (
                <span className="bg-[#EBF7E0] text-[#4C9E00] text-xs font-bold px-2 py-0.5 rounded-full">
                  {locations.length}
                </span>
              )}
            </h2>
            <button
              onClick={() => refreshData()}
              className="text-gray-400 hover:text-[#4C9E00] transition p-1 rounded"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>

          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white border border-gray-100 rounded-xl p-4 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/3 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : locations.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <p>No locations found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {locations.map((loc) => (
                <button
                  key={loc.id}
                  onClick={() => handleSelectLocation(loc)}
                  className="w-full text-left bg-white border border-gray-100 rounded-xl p-4 hover:border-[#4C9E00] hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 group-hover:text-[#4C9E00] transition">
                          {loc.Area}
                        </span>
                        <span className="bg-[#EBF7E0] text-[#4C9E00] text-xs px-2 py-0.5 rounded-full font-medium">
                          {loc.AreaType}
                        </span>
                        {loc.AllRedTaped === 'Yes' && (
                          <span className="bg-red-50 text-red-600 text-xs px-2 py-0.5 rounded-full font-medium">
                            🔴 Red Taped
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        {loc.LastInspectedDate
                          ? `Inspected ${formatDate(loc.LastInspectedDate)} by ${loc.InspectedBy ?? '—'}`
                          : 'No recorded inspections'}
                      </p>
                    </div>

                    {/* Concern count badge */}
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {(loc.NumberOfConcerns ?? 0) > 0 ? (
                        <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                          {loc.NumberOfConcerns} concern{(loc.NumberOfConcerns ?? 0) > 1 ? 's' : ''}
                        </span>
                      ) : (
                        <span className="bg-green-50 text-[#4C9E00] text-xs font-medium px-2 py-0.5 rounded-full">
                          No concerns
                        </span>
                      )}
                      {loc.NextInspectionDueBy && (
                        <span className="text-xs text-gray-400">
                          Due {formatDate(loc.NextInspectionDueBy)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Chevron */}
                  <div className="flex justify-end mt-1 opacity-0 group-hover:opacity-100 transition">
                    <svg className="w-4 h-4 text-[#4C9E00]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Offline notice ───────────────────────────────────────────────── */}
      {!isOnline && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <div>
            <p className="font-semibold text-amber-800 text-sm">You are offline</p>
            <p className="text-amber-700 text-sm mt-0.5">
              Inspections will be saved as drafts and uploaded when you reconnect.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
